// Entry point for all reusable API calls
import axios from 'axios';

// Automatically detect the correct backend URL
export function getBackendURL() {
  // If environment variable is set, use it
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  const hostname = window.location.hostname;
  
  // Local development: localhost or 127.0.0.1
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:3001';
  }

  // LAN development: private IP ranges
  if (/^(192\.168\.|10\.|172\.(1[6-9]|2\d|3[01])\.)/.test(hostname)) {
    return `http://${hostname}:3001`;
  }
  
  // Production: any public domain → Azure Front Door backend
  return 'https://object-link-api-hfgganagg2crcmg5.z02.azurefd.net';
}

const baseURL = getBackendURL();
console.log('🔗 API Base URL:', baseURL);

const api = axios.create({
  baseURL: baseURL,
  withCredentials: true,
  timeout: 30000, // Increased to 30 seconds for AI operations
});

// Public API instance without authentication
const publicApi = axios.create({
  baseURL: baseURL,
  timeout: 10000,
});

// Add JWT token to every request if available
api.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// Let React transition to the login screen without restarting the document.
api.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401 && localStorage.getItem('token')) {
      localStorage.removeItem('token');
      window.dispatchEvent(new Event('auth:unauthorized'));
    }
    return Promise.reject(error);
  }
);

export default api;
export { publicApi };

// News API functions  
export const newsAPI = {
  // News für ein Objekt abrufen (paginiert)
  getNews: (objectId, isVisitor = false, page = 1, limit = 20) => {
    const params = { _t: Date.now(), page, limit };
    if (isVisitor) {
      return publicApi.get(`/api/objects/public/${objectId}/news`, { params });
    }
    return api.get(`/api/objects/${objectId}/news`, { params });
  },
  
  // News erstellen (mit direktem File-Upload)
  createNews: (newsData) => {
    const formData = new FormData();
    
    // Basic fields
    formData.append('title', newsData.title);
    formData.append('content', newsData.content);
    formData.append('taggedUsers', JSON.stringify(newsData.taggedUsers || []));
    if (newsData.expiryDate) {
      formData.append('expiryDate', newsData.expiryDate);
    }
    
    // Attach files directly
    if (newsData.attachments && newsData.attachments.length > 0) {
      newsData.attachments.forEach(file => {
        formData.append('attachments', file);
      });
    }
    
    return api.post(`/api/objects/${newsData.objectId}/news`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  
  // News bearbeiten (mit direktem File-Upload)
  updateNews: (newsId, newsData) => {
    const formData = new FormData();
    
    // Basic fields
    formData.append('title', newsData.title);
    formData.append('content', newsData.content);
    formData.append('taggedUsers', JSON.stringify(newsData.taggedUsers || []));
    if (newsData.expiryDate) {
      formData.append('expiryDate', newsData.expiryDate);
    }
    
    // Control existing attachments
    formData.append('keepExistingAttachments', newsData.keepExistingAttachments || 'false');
    
    // Attach new files
    if (newsData.attachments && newsData.attachments.length > 0) {
      newsData.attachments.forEach(file => {
        formData.append('attachments', file);
      });
    }
    
    return api.put(`/api/objects/${newsData.objectId}/news/${newsId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  
  // News löschen
  deleteNews: (objectId, newsId) => api.delete(`/api/objects/${objectId}/news/${newsId}`),
  
  // News-Berechtigungen aktualisieren (placeholder for future implementation)
  updateNewsPermissions: (newsId, permissions) => 
    Promise.resolve({ data: { success: true } })
};

// Document Processing API functions
export const documentAPI = {
  // Process document with AI Vision
  processDocument: (file) => {
    const formData = new FormData();
    formData.append('document', file);
    formData.append('method', 'vision'); // Always use vision
    
    return api.post('/objects/from-document', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 60000, // Longer timeout for document processing (60 seconds)
    });
  },
  
  // Create objects from extracted data
  createFromExtracted: (objectsData, parentId) => {
    return api.post('/objects/create-from-extracted', {
      objects: objectsData,
      parentId
    });
  },

  // Scan document, upload to object, and create AI summary note
  scanSummarize: (objectId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/objects/${objectId}/scan-summarize`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 90000,
    });
  },

  // Analyze a photo to extract object data (single or multi)
  analyzePhoto: (file, { multi = false } = {}) => {
    const formData = new FormData();
    formData.append('photo', file, file.name || 'camera-capture.jpg');
    if (multi) formData.append('multi', 'true');
    return api.post('/objects/from-photo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    });
  }
};

// SSE stream parser for AI Chat responses
async function parseSSEStream(response, callbacks) {
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let currentEvent = null;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || ''; // Keep incomplete last line in buffer

    for (const line of lines) {
      if (line.startsWith('event: ')) {
        currentEvent = line.slice(7).trim();
      } else if (line.startsWith('data: ') && currentEvent) {
        try {
          const data = JSON.parse(line.slice(6));
          switch (currentEvent) {
            case 'token': callbacks.onToken?.(data.content); break;
            case 'tool_call': callbacks.onToolCall?.(data); break;
            case 'tool_result': callbacks.onToolResult?.(data); break;
            case 'confirmation_required': callbacks.onConfirmation?.(data); break;
            case 'conversation_id': callbacks.onConversationId?.(data.id); break;
            case 'title': callbacks.onTitle?.(data.title); break;
            case 'rag_debug': callbacks.onRagDebug?.(data); break;
            case 'context_switch': callbacks.onContextSwitch?.(data); break;
            case 'done': callbacks.onDone?.(data); break;
            case 'error': callbacks.onError?.(data.message); break;
            default: break;
          }
        } catch (e) { /* ignore parse errors */ }
        currentEvent = null;
      } else if (line === '') {
        currentEvent = null;
      }
    }
  }
}

// AI Chat API functions
export const aiChatAPI = {
  listConversations: (objectId) => api.get(`/api/ai-chat/${objectId}/conversations`),
  getConversation: (objectId, convId) => api.get(`/api/ai-chat/${objectId}/conversations/${convId}`),
  deleteConversation: (objectId, convId) => api.delete(`/api/ai-chat/${objectId}/conversations/${convId}`),
  
  // Send message via SSE stream (returns AbortController for cancellation)
  sendMessage: (objectId, convId, message, { onToken, onToolCall, onToolResult, onConfirmation, onConversationId, onRagDebug, onDone, onError }, pendingFileKey = null) => {
    const controller = new AbortController();
    const token = localStorage.getItem('token');
    
    const parseSSECallbacks = { onToken, onToolCall, onToolResult, onConfirmation, onConversationId, onRagDebug, onDone, onError };
    
    fetch(`${baseURL}/api/ai-chat/${objectId}/conversations/${convId}/message`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ message, ...(pendingFileKey ? { pendingFileKey } : {}) }),
      signal: controller.signal
    }).then(async response => {
      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: 'Verbindungsfehler' }));
        onError?.(err.error || 'Fehler beim Senden');
        return;
      }
      await parseSSEStream(response, parseSSECallbacks);
    }).catch(err => {
      if (err.name !== 'AbortError') {
        onError?.(err.message || 'Netzwerkfehler');
      }
    });
    
    return controller;
  },

  // Send message with image via SSE stream
  sendImageMessage: (objectId, convId, message, imageFile, callbacks) => {
    const controller = new AbortController();
    const token = localStorage.getItem('token');
    
    const formData = new FormData();
    formData.append('message', message);
    formData.append('image', imageFile);
    
    fetch(`${baseURL}/api/ai-chat/${objectId}/conversations/${convId}/image`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData,
      signal: controller.signal
    }).then(async response => {
      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: 'Verbindungsfehler' }));
        callbacks.onError?.(err.error || 'Fehler beim Senden');
        return;
      }
      await parseSSEStream(response, callbacks);
    }).catch(err => {
      if (err.name !== 'AbortError') {
        callbacks.onError?.(err.message || 'Netzwerkfehler');
      }
    });
    
    return controller;
  },

  confirmAction: (objectId, convId, toolCallId, approved) =>
    api.post(`/api/ai-chat/${objectId}/conversations/${convId}/confirm`, { toolCallId, approved }, {
      timeout: 120000 // Long timeout for SSE follow-up response
    }),

  summarizeConversation: (objectId, convId) =>
    api.post(`/api/ai-chat/${objectId}/conversations/${convId}/summarize`),

  saveAsNote: (objectId, convId, { title, content, section }) =>
    api.post(`/api/ai-chat/${objectId}/conversations/${convId}/save-as-note`, { title, content, section }),

  uploadFile: (objectId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/api/ai-chat/${objectId}/upload-file`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  }
};

// Global AI Chat API functions
export const globalAiChatAPI = {
  listConversations: () => api.get('/api/global-ai-chat/conversations'),
  getConversation: (convId) => api.get(`/api/global-ai-chat/conversations/${convId}`),
  deleteConversation: (convId) => api.delete(`/api/global-ai-chat/conversations/${convId}`),

  sendMessage: (convId, message, { onToken, onToolCall, onToolResult, onConversationId, onTitle, onRagDebug, onDone, onError }, pendingFileKey = null) => {
    const controller = new AbortController();
    const token = localStorage.getItem('token');
    const callbacks = { onToken, onToolCall, onToolResult, onConversationId, onTitle, onRagDebug, onDone, onError };

    fetch(`${baseURL}/api/global-ai-chat/conversations/${convId}/message`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ message, ...(pendingFileKey ? { pendingFileKey } : {}) }),
      signal: controller.signal
    }).then(async response => {
      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: 'Verbindungsfehler' }));
        onError?.(err.error || 'Fehler beim Senden');
        return;
      }
      await parseSSEStream(response, callbacks);
    }).catch(err => {
      if (err.name !== 'AbortError') {
        onError?.(err.message || 'Netzwerkfehler');
      }
    });

    return controller;
  },

  sendImageMessage: (convId, message, imageFile, callbacks) => {
    const controller = new AbortController();
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('message', message);
    formData.append('image', imageFile);
    fetch(`${baseURL}/api/global-ai-chat/conversations/${convId}/image`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData,
      signal: controller.signal
    }).then(async response => {
      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: 'Verbindungsfehler' }));
        callbacks.onError?.(err.error || 'Fehler beim Senden');
        return;
      }
      await parseSSEStream(response, callbacks);
    }).catch(err => {
      if (err.name !== 'AbortError') { callbacks.onError?.(err.message || 'Netzwerkfehler'); }
    });
    return controller;
  },

  uploadFile: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/api/global-ai-chat/upload-file', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  }
};

// Unified Chat API — single chat with OBJECT_MODE and DISCOVERY_MODE
export const unifiedChatAPI = {
  listConversations: (objectId) =>
    objectId
      ? api.get(`/api/unified-chat/conversations?objectId=${objectId}`)
      : api.get('/api/unified-chat/conversations'),

  getConversation: (convId) => api.get(`/api/unified-chat/conversations/${convId}`),
  deleteConversation: (convId) => api.delete(`/api/unified-chat/conversations/${convId}`),

  /**
   * Send a text message via SSE.
   * @param {string} convId - conversation ID or 'new'
   * @param {string} message
   * @param {Object} callbacks - { objectId?, onToken, onToolCall, onToolResult,
   *   onConfirmation, onContextSwitch, onConversationId, onTitle, onRagDebug, onDone, onError }
   * @returns {AbortController}
   */
  sendMessage: (convId, message, { objectId = null, pendingFileKey = null, onToken, onToolCall, onToolResult, onConfirmation, onContextSwitch, onConversationId, onTitle, onRagDebug, onDone, onError } = {}) => {
    const controller = new AbortController();
    const token = localStorage.getItem('token');
    const callbacks = { onToken, onToolCall, onToolResult, onConfirmation, onContextSwitch, onConversationId, onTitle, onRagDebug, onDone, onError };

    fetch(`${baseURL}/api/unified-chat/conversations/${convId}/message`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ message, objectId, ...(pendingFileKey ? { pendingFileKey } : {}) }),
      signal: controller.signal
    }).then(async response => {
      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: 'Verbindungsfehler' }));
        onError?.(err.error || 'Fehler beim Senden');
        return;
      }
      await parseSSEStream(response, callbacks);
    }).catch(err => {
      if (err.name !== 'AbortError') {
        onError?.(err.message || 'Netzwerkfehler');
      }
    });

    return controller;
  },

  /**
   * Send a message with an image (vision mode).
   */
  sendImageMessage: (convId, message, imageFile, { objectId = null, ...callbacks } = {}) => {
    const controller = new AbortController();
    const token = localStorage.getItem('token');

    const formData = new FormData();
    formData.append('message', message);
    formData.append('image', imageFile);
    if (objectId) formData.append('objectId', objectId);

    fetch(`${baseURL}/api/unified-chat/conversations/${convId}/image`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData,
      signal: controller.signal
    }).then(async response => {
      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: 'Verbindungsfehler' }));
        callbacks.onError?.(err.error || 'Fehler beim Senden');
        return;
      }
      await parseSSEStream(response, callbacks);
    }).catch(err => {
      if (err.name !== 'AbortError') {
        callbacks.onError?.(err.message || 'Netzwerkfehler');
      }
    });

    return controller;
  },

  confirmAction: (convId, toolCallId, approved) =>
    api.post(`/api/unified-chat/conversations/${convId}/confirm`, { toolCallId, approved }),

  confirmActionsBatch: (convId, toolCallIds, approved) =>
    api.post(`/api/unified-chat/conversations/${convId}/confirm-batch`, { toolCallIds, approved }),

  uploadFile: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/api/unified-chat/upload-file', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  }
};
